<template>
  <div class="evaluation-base">
    <div class="left-panel">
      <evaluation-header-info
        @show-info="currentSectionIndex = null"
        :evaluation="evaluation"
        :branch="branch"
      />
      <scrollable-container>
        <evaluation-sections-navigator
          @sectionSelected="onSelectSection"
          :current-section-index="currentSectionIndex"
          :items="evaluation.template.sections"
        />
      </scrollable-container>
    </div>
    <div class="center-panel">
      <evaluation-top-bar
        v-if="currentSection"
        :current-section-index="currentSectionIndex"
        :sections-count="evaluation.template.sections.length"
        @save="onSave"
        @partial-save="onPartialSave"
        @previous="previousSection"
        @next="nextSection"
      />
      <scrollable-container>
        <evaluation-content
          v-if="currentSection"
          :branch="branch"
          :section="currentSection"
          :index="currentSectionIndex"
        />
        <evaluation-info
          v-else
          :branch="branch"
          :assignment="assignment"
          :layout="layout"
          :evaluation="evaluation"
          @start="startEvaluation"
        />
      </scrollable-container>
    </div>
    <evaluation-finish
      @close="onFinishedClose"
      @share-evaluation="onShareEvaluation"
      :base-score="evaluation.baseScore"
      :score="evaluation.score"
      v-if="finished"
    />
  </div>
</template>

<script>
import EvaluationHeaderInfo from '@/modules/evaluation/components/layout/evaluation-header-info'
import ScrollableContainer from '@/components/scrollable-container'
import EvaluationSectionsNavigator from '@/modules/evaluation/components/layout/evaluation-sections-navigator'
import EvaluationContent from '@/modules/evaluation/components/layout/evaluation-content'
import EvaluationTopBar from '@/modules/evaluation/components/layout/evaluation-top-bar'
import EvaluationInfo from '@/modules/evaluation/components/layout/evaluation-info'
import EvaluationFinish from '@/modules/evaluation/components/layout/evaluation-finish'

export default {
  name: 'EvaluationBase',
  components: {
    EvaluationFinish,
    EvaluationInfo,
    EvaluationTopBar,
    EvaluationContent,
    EvaluationSectionsNavigator,
    ScrollableContainer,
    EvaluationHeaderInfo
  },
  props: {
    evaluation: {
      type: Object,
      required: true
    },
    branch: {
      type: Object,
      required: true
    },
    assignment: {
      type: Object,
      required: true
    },
    layout: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      currentSectionIndex: null,
      finished: false
    }
  },
  provide () {
    return {
      evaluationId: this.evaluation.id
    }
  },
  computed: {
    currentSection () {
      return this.evaluation.template.sections[this.currentSectionIndex] || null
    }
  },
  methods: {
    onFinishedClose () {
      this.$router.push({
        name: 'branches-view',
        params: { id: this.branch.id }
      })
    },
    onShareEvaluation () {
      this.$router.push({
        name: 'branches-view',
        params: { id: this.branch.id }
      })
    },
    onSave () {
      if (this.sectionSolved()) {
        let baseScore = 0
        let score = 0
        let loader = this.$loading()
        this.finished = true
        for (let section of this.evaluation.template.sections) {
          for (let child of section.$children) {
            if (child.$group === 'subsection') {
              for (let question of child.$children) {
                baseScore += question.$baseScore
                score += question.$points
              }
            } else {
              baseScore += child.$baseScore
              score += child.$points
            }
          }
        }
        this.evaluation.score = score
        this.evaluation.baseScore = baseScore
        this.evaluation.status = 'complete'
        this.$store
          .dispatch('evaluations/save', this.evaluation)
          .then(() => {
            this.$message.success('La evaluación ha sido almacenada con éxito')
            this.finished = true
          })
          .catch(() => {
            this.$message.error('Hubo un problema al guardar la evaluación')
          })
          .finally(() => {
            loader.close()
          })
      }
    },
    onPartialSave () {
      let baseScore = 0
      let score = 0
      let loader = this.$loading()
      this.finished = false
      for (let section of this.evaluation.template.sections) {
        for (let child of section.$children) {
          if (child.$group === 'subsection') {
            for (let question of child.$children) {
              baseScore += question.$baseScore
              score += question.$points
            }
          } else {
            baseScore += child.$baseScore
            score += child.$points
          }
        }
      }
      this.evaluation.score = score
      this.evaluation.baseScore = baseScore
      this.$store
        .dispatch('evaluations/save', this.evaluation)
        .then(() => {
          this.$message.success('La evaluación ha sido almacenada con éxito')
          this.finished = false
        })
        .catch(() => {
          this.$message.error('Hubo un problema al guardar la evaluación')
        })
        .finally(() => {
          loader.close()
        })
    },
    /**
     * Verifies if a section is solved
     * Returns true if a question has no answer
     */
    sectionSolved () {
      if (this.currentSection) {
        for (let item of this.currentSection.$children) {
          if (item.$group === 'question') {
            if (this.isQuestionUnsolved(item) || this.isLackingEvidence(item)) {
              return false
            }
          } else {
            for (let question of item.$children) {
              if (
                this.isQuestionUnsolved(question) ||
                this.isLackingEvidence(question)
              ) {
                return false
              }
            }
          }
        }
      }
      return true
    },
    isLackingEvidence (question) {
      if (
        (question.evidenceRequired || question.innerEvidenceRequired) &&
        (!question.evidence || !question.evidence.length)
      ) {
        this.$message.error('Adjunte la evidencia requerida para continuar')
        return true
      }
      return false
    },
    isQuestionUnsolved (question) {
      if (question.type !== 'multiple' && question.type !== 'range') {
        if (question.$result === null || !question.$result.length) {
          this.$message.error(
            'Conteste todas las preguntas de la sección para continuar'
          )
          return true
        }
      }
      console.log(question.$result)
      return false
    },
    previousSection () {
      this.onSelectSection(this.currentSectionIndex - 1, false)
    },
    nextSection () {
      if (this.sectionSolved()) {
        this.onSelectSection(this.currentSectionIndex + 1)
      }
    },
    startEvaluation () {
      this.onSelectSection(0)
    },
    onSelectSection (sectionIndex) {
      if (sectionIndex < this.currentSectionIndex) {
        this.$set(this.currentSection, '$selected', false)
        this.currentSectionIndex = sectionIndex
        this.$set(this.currentSection, '$selected', true)
      } else {
        if (this.sectionSolved()) {
          if (this.currentSection) {
            this.$set(this.currentSection, '$selected', false)
          }
          this.currentSectionIndex = sectionIndex
          this.$set(this.currentSection, '$selected', true)
        }
      }
    }
  }
}
</script>

<style
  lang="scss"
  scoped
>
.evaluation-base {
  width: 100vw;
  display: flex;
  align-items: flex-start;
  justify-content: stretch;
  .left-panel {
    width: 315px;
    min-width: 315px;
    max-width: 315px;
    height: 100vh;
    border-right: 1px solid #dddddd;
  }
  .center-panel {
    height: 100vh;
    max-height: 100vh;
    min-height: 100vh;
    flex-grow: 1;
    max-width: 100%;
    overflow: hidden;
    background-color: #fff;
    position: relative;
  }
}

@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
  .evaluation-base {
    .right-panel {
      display: none;
    }
    .center-panel {
      max-width: 709px;
      overflow: auto;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/evaluation/evaluation-base.vue